import {isEqualTrackRef, isTrackReference, isWeb, log, setupParticipantName} from '@livekit/components-core';
import {RoomEvent, Track} from 'livekit-client';
import * as React from 'react';
import {
    CarouselLayout,
    ConnectionStateToast,
    FocusLayout,
    FocusLayoutContainer,
    GridLayout,
    LayoutContextProvider,
    ParticipantTile,
    RoomAudioRenderer,
    useCreateLayoutContext,
    usePinnedTracks,
    useTracks,
    Chat, useEnsureParticipant, useLocalParticipant, useEnsureTrackRef,
} from "@livekit/components-react";
import './videoConference.css';
import {CustomParticipantTile} from "./CustomParticipantTile";
import {CustomVideoTrack} from "./CustomVideoTrack";

export function NewCustomVideoConference({
                                             recordingInProgress,
                                             startRecording,
                                             setRecordingDisabled,
                                             stopRecording,
                                             localRecordingInProgress,
                                             nameStyle,
                                             videoMirrored,
                                             hostIdentity,
                                             themeBackgroundColor,
                                             brandLogoUrl,
                                             chatMessageFormatter,
                                             chatMessageDecoder,
                                             chatMessageEncoder,
                                             isSoloSession = false,
                                             ...props
                                         }) {
    const [widgetState, setWidgetState] = React.useState({
        showChat: false,
        unreadMessages: 0,
    });
    const lastAutoFocusedScreenShareTrack = React.useRef(null);

    const tracks = useTracks(
        [
            {source: Track.Source.Camera, withPlaceholder: true},
            {source: Track.Source.ScreenShare, withPlaceholder: false},
        ],
        {updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false},
    );

    const internalAgentParticipants = ["TimerAgent", "RecordingAgent", "Soundboard"];
    const {cameraTrack, microphoneTrack} = useLocalParticipant();

    const hasLocalVideo = cameraTrack && !cameraTrack.isMuted;
    const hasLocalAudio = microphoneTrack && !microphoneTrack.isMuted;

    const localScreenShareTrack = tracks.find(
        (t) => t.participant.isLocal && t.publication?.source === Track.Source.ScreenShare,
    );

    const overlayCameraOnScreenShare =
        isSoloSession && !!localScreenShareTrack && hasLocalVideo;

    let visibleTracks = tracks.filter((track) => !internalAgentParticipants.includes(track.participant.identity));

    if (
        isSoloSession &&
        (!(hasLocalVideo || hasLocalAudio) || !!localScreenShareTrack)
    ) {
        visibleTracks = visibleTracks.filter(
            (track) => !(track.participant.isLocal && track.publication?.source === Track.Source.Camera),
        );
    }

    const localCameraTrackRef = tracks.find(
        (t) => t.participant.isLocal && t.publication?.source === Track.Source.Camera,
    );

    const widgetUpdate = (state) => {
        log.debug('updating widget state', state);
        setWidgetState(state);
    };

    const layoutContext = useCreateLayoutContext();

    const screenShareTracks = tracks
        .filter(isTrackReference)
        .filter((track) => track.publication.source === Track.Source.ScreenShare);

    const focusTrack = usePinnedTracks(layoutContext)?.[0];
    const carouselTracks = visibleTracks.filter((track) => !isEqualTrackRef(track, focusTrack));

    const showBarVisualizer = hasLocalAudio && !hasLocalVideo && isSoloSession && screenShareTracks.length === 0 && visibleTracks.length === 1;

    React.useEffect(() => {
        // If screen share tracks are published, and no pin is set explicitly, auto set the screen share. Ignore in solo sessions.
        if (
            screenShareTracks.some((track) => track.publication.isSubscribed) &&
            lastAutoFocusedScreenShareTrack.current === null && !isSoloSession
        ) {
            log.debug('Auto set screen share focus:', {newScreenShareTrack: screenShareTracks[0]});
            layoutContext.pin.dispatch?.({msg: 'set_pin', trackReference: screenShareTracks[0]});
            lastAutoFocusedScreenShareTrack.current = screenShareTracks[0];
        } else if (
            lastAutoFocusedScreenShareTrack.current &&
            !screenShareTracks.some(
                (track) =>
                    track.publication.trackSid ===
                    lastAutoFocusedScreenShareTrack.current?.publication?.trackSid,
            )
        ) {
            log.debug('Auto clearing screen share focus.');
            layoutContext.pin.dispatch?.({msg: 'clear_pin'});
            lastAutoFocusedScreenShareTrack.current = null;
        }
    }, [
        screenShareTracks
            .map((ref) => `${ref.publication.trackSid}_${ref.publication.isSubscribed}`)
            .join(),
        focusTrack?.publication?.trackSid,
    ]);

    if (!visibleTracks.length && isSoloSession) {
        setRecordingDisabled(true);
        return (
            <div className="libretto-video-conference" style={{backgroundColor: themeBackgroundColor, justifyContent: "center", alignItems: "center"}} {...props}>
                <div style={{color: "#1a1a1a"}}>Please select an input below</div>
            </div>
        )
    }

    setRecordingDisabled(false);
    return (
        <div className="libretto-video-conference" style={{backgroundColor: themeBackgroundColor}} {...props}>
            {isWeb() && (
                <LayoutContextProvider
                    value={layoutContext}
                    // onPinChange={handleFocusStateChange}
                    onWidgetChange={widgetUpdate}
                >
                    <div className="libretto-video-conference-inner">
                        {!focusTrack ? (
                            <div className="libretto-grid-layout-wrapper">
                                <GridLayout tracks={visibleTracks}>
                                    <CustomParticipantTile nameStyle={nameStyle}
                                                           showBarVisualizer={showBarVisualizer}
                                                           hostIdentity={hostIdentity}
                                                           brandLogoUrl={brandLogoUrl}
                                                           videoMirrored={videoMirrored}
                                                           isSoloSession={isSoloSession}/>
                                </GridLayout>
                                {overlayCameraOnScreenShare && localCameraTrackRef && (
                                    <div className="libretto-camera-overlay">
                                        <CustomVideoTrack trackRef={localCameraTrackRef}
                                                          videoMirrored={videoMirrored}
                                                          isSoloSession={isSoloSession}/>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="libretto-focus-layout-wrapper">
                                <FocusLayoutContainer>
                                    <CarouselLayout tracks={carouselTracks}>
                                        <CustomParticipantTile nameStyle={nameStyle}
                                                               showBarVisualizer={showBarVisualizer}
                                                               hostIdentity={hostIdentity}
                                                               brandLogoUrl={brandLogoUrl}
                                                               videoMirrored={videoMirrored}
                                                               isSoloSession={isSoloSession}/>
                                    </CarouselLayout>
                                    {focusTrack && <FocusLayout trackRef={focusTrack}/>}
                                    {overlayCameraOnScreenShare && localCameraTrackRef && (
                                        <div className="libretto-camera-overlay">
                                            <CustomVideoTrack trackRef={localCameraTrackRef}
                                                              videoMirrored={videoMirrored}
                                                              isSoloSession={isSoloSession}/>
                                        </div>
                                    )}
                                </FocusLayoutContainer>
                            </div>
                        )}
                    </div>
                </LayoutContextProvider>
            )}
            <RoomAudioRenderer/>
            <ConnectionStateToast/>
        </div>
    );
}
